import React, { useCallback, useMemo } from 'react';
import { CodeEditor, Dropdown, Input, InputNumber, InputTime } from '@cognitiv/cyprus-ui';
import classNames from 'classnames';
import { selectCpuArchitecturesList } from 'ducks/cpu-architectures-list/selectors';
import { useAppSelector } from 'ducks/hooks';
import { selectLocationGroupsList } from 'ducks/location-groups-list/selectors';
import { selectPreventPreemptionsList } from 'ducks/prevent-preemptions-list/selectors';
import { selectProject } from 'ducks/project/selectors';
import { selectResultMethodsList } from 'ducks/result-methods-list/selectors';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { useJobDefinitionContext } from 'modals/job-definition/context/JobDefinitionContext';
import { getValidationError } from 'utils/errors';

import cn from 'modals/job-definition/components/Components.module.scss';

export const RunJobDefinitionConfiguration = () => {
  const cpu_architecture = useAppSelector(selectCpuArchitecturesList);
  const location_groups = useAppSelector(selectLocationGroupsList);
  const prevent_preemption = useAppSelector(selectPreventPreemptionsList);
  const project = useAppSelector(selectProject);
  const result_methods = useAppSelector(selectResultMethodsList);

  const {
    form,
    errors: baseErrors,
    onChange,
    onTimeoutSecondsChange,
    onPreventPreemptionSelect,
    onCPUArchitectureSelect,
    onLocationGroupSelect,
    onResultMethodSelect,
  } = useJobDefinitionContext();

  const onDockerImageChange = useCallback((value: string) => onChange({ docker_image: value }), [onChange]);

  const onStartupCommandChange = useCallback((value: string) => onChange({ startup_command: value }), [onChange]);
  const onCPUChange = useCallback((input: { value: number }) => onChange({ cpu: input.value }), [onChange]);
  const onMemoryChange = useCallback((input: { value: number }) => onChange({ memory_gb: input.value }), [onChange]);
  const onGPUChange = useCallback((input: { value: number }) => onChange({ gpu: input.value }), [onChange]);
  const onSharedMemoryChange = useCallback(
    (input: { value: number }) => onChange({ shared_memory_mb: input.value }),
    [onChange],
  );
  const onStorageChange = useCallback((input: { value: number }) => onChange({ storage_gb: input.value }), [onChange]);
  const onMaxRetriesChange = useCallback(
    (input: { value: number }) => onChange({ max_retries: input.value }),
    [onChange],
  );
  const onSuccessTextChange = useCallback((value: string) => onChange({ stdout_success_text: value }), [onChange]);
  const onBillingIdentifierChange = useCallback((value: string) => onChange({ billing_identifier: value }), [onChange]);

  const {
    billing_identifier,
    docker_image,
    job_definition_id,
    startup_command,
    cpu,
    gpu,
    memory_gb,
    prevent_preemption_name,
    shared_memory_mb,
    timeout_seconds_masked,
    storage_gb,
    max_retries,
    cpu_architecture_name,
    location_group_name,
    result_method_name,
    result_method_id,
    stdout_success_text,
  } = form;

  const errors = useMemo(() => {
    if (result_method_id !== 2) {
      return baseErrors.filter((error) => error.key !== 'stdout_success_text');
    }

    return baseErrors;
  }, [baseErrors, result_method_id]);

  const billing_identifier_placeholder = billing_identifier || project.default_billing_identifier;
  return (
    <>
      <Input
        auto_focus
        label="Docker Image"
        value={docker_image}
        onChange={onDockerImageChange}
        error_message={errors.find((error) => error.key === 'docker_image')?.error}
        error_level={getValidationError({ errors, key: 'docker_image' }) ? ERROR_LEVEL.ERROR : undefined}
      />
      <CodeEditor
        key={job_definition_id}
        label="Startup Command"
        unique_identifier="run_startup_command"
        value={startup_command || ''}
        languages={['shell']}
        margin="10px 0px 0px 0px"
        height="400px"
        min_height="172px"
        onChange={onStartupCommandChange}
      />
      <div className={cn.jobInputsWrapper}>
        <div className={cn.jobInputs}>
          <InputNumber
            label="CPU"
            value={cpu}
            margin="10px 0px 0px 0px"
            onChange={onCPUChange}
            error_message={errors.find((error) => error.key === 'cpu')?.error}
            error_level={getValidationError({ errors, key: 'cpu' }) ? ERROR_LEVEL.ERROR : undefined}
          />
          <InputNumber
            label="Memory GB"
            margin="10px 0px 0px 0px"
            value={memory_gb}
            onChange={onMemoryChange}
            error_message={errors.find((error) => error.key === 'memory_gb')?.error}
            error_level={getValidationError({ errors, key: 'memory_gb' }) ? ERROR_LEVEL.ERROR : undefined}
          />
          <Dropdown
            options={prevent_preemption}
            label="Prevent Preemption"
            option_key="name"
            value={prevent_preemption_name}
            margin="10px 0px 0px 0px"
            onSelect={onPreventPreemptionSelect}
          />
        </div>
        <div className={cn.jobInputs}>
          <InputNumber
            label="GPU"
            margin="10px 0px 0px 0px"
            value={gpu}
            onChange={onGPUChange}
            error_message={errors.find((error) => error.key === 'gpu')?.error}
            error_level={getValidationError({ errors, key: 'gpu' }) ? ERROR_LEVEL.ERROR : undefined}
          />
          <InputNumber
            label="Shared Memory MB"
            margin="10px 0px 0px 0px"
            value={shared_memory_mb}
            onChange={onSharedMemoryChange}
            error_message={errors.find((error) => error.key === 'shared_memory_mb')?.error}
            error_level={getValidationError({ errors, key: 'shared_memory_mb' }) ? ERROR_LEVEL.ERROR : undefined}
          />
          <InputTime
            label="Timeout (hh:mm:ss)"
            value={timeout_seconds_masked}
            margin="10px 0px 0px 0px"
            onChange={onTimeoutSecondsChange}
            error_message={errors.find((error) => error.key === 'timeout_seconds')?.error}
            error_level={getValidationError({ errors, key: 'timeout_seconds' }) ? ERROR_LEVEL.ERROR : undefined}
          />
        </div>
        <div className={cn.jobInputs}>
          <InputNumber
            label="Storage GB"
            margin="10px 0px 0px 0px"
            value={storage_gb}
            onChange={onStorageChange}
            error_message={errors.find((error) => error.key === 'storage_gb')?.error}
            error_level={getValidationError({ errors, key: 'storage_gb' }) ? ERROR_LEVEL.ERROR : undefined}
          />
          <InputNumber
            label="Max Retries"
            value={max_retries}
            margin="10px 0px 0px 0px"
            onChange={onMaxRetriesChange}
            error_message={errors.find((error) => error.key === 'max_retries')?.error}
            error_level={getValidationError({ errors, key: 'max_retries' }) ? ERROR_LEVEL.ERROR : undefined}
          />
          <Dropdown
            options={cpu_architecture}
            label="CPU Architecture"
            option_key="cpu_architecture_name"
            value={cpu_architecture_name}
            margin="10px 0px 0px 0px"
            onSelect={onCPUArchitectureSelect}
            error_message={errors.find((error) => error.key === 'cpu_architecture_name')?.error}
            error_level={getValidationError({ errors, key: 'cpu_architecture_name' }) ? ERROR_LEVEL.ERROR : undefined}
          />
        </div>
        <div className={classNames(cn.jobInputsDropdowns)}>
          <div className={cn.dropdowns}>
            <Dropdown
              options={location_groups}
              label="Location Group"
              scroll_height={250}
              option_key="location_group_name"
              placeholder="Select..."
              value={location_group_name}
              margin="10px 0px 0px 0px"
              onSelect={onLocationGroupSelect}
              error_message={errors.find((error) => error.key === 'location_group_name')?.error}
              error_level={getValidationError({ errors, key: 'location_group_name' }) ? ERROR_LEVEL.ERROR : undefined}
            />
          </div>
          <div className={cn.dropdowns}>
            <div className={cn.results}>
              <Dropdown
                options={result_methods}
                label="Result Method"
                option_key="result_method_name"
                placeholder="Select..."
                value={result_method_name}
                margin="10px 0px 0px 0px"
                onSelect={onResultMethodSelect}
                error_message={errors.find((error) => error.key === 'result_method_name')?.error}
                error_level={getValidationError({ errors, key: 'result_method_name' }) ? ERROR_LEVEL.ERROR : undefined}
              />
            </div>
            {result_method_id === 2 && (
              <div className={cn.success}>
                <Input
                  label="Success Text"
                  value={stdout_success_text}
                  disabled={result_method_id !== 2}
                  margin="10px 0px 0px 0px"
                  onChange={onSuccessTextChange}
                  error_message={errors.find((error) => error.key === 'stdout_success_text')?.error}
                  error_level={
                    getValidationError({ errors, key: 'stdout_success_text' }) ? ERROR_LEVEL.ERROR : undefined
                  }
                />
              </div>
            )}
          </div>
          <Input
            label="Billing Identifier"
            margin="10px 0px 0px 0px"
            placeholder={billing_identifier_placeholder}
            value={billing_identifier}
            onChange={onBillingIdentifierChange}
            error_message={errors.find((error) => error.key === 'billing_identifier')?.error}
            error_level={getValidationError({ errors, key: 'billing_identifier' }) ? ERROR_LEVEL.ERROR : undefined}
          />
        </div>
      </div>
    </>
  );
};
